import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Loader2, Link2, Check } from 'lucide-react';
import { ClientHeader } from '../components/client-detail/ClientHeader';
import { ClientShootsSection } from '../components/client-detail/ClientShootsSection';
import { ClientMeetingsSection } from '../components/client-detail/ClientMeetingsSection';
import { ClientDocumentsSection } from '../components/client-detail/ClientDocumentsSection';
import { ClientMoneySection } from '../components/client-detail/ClientMoneySection';
import { ClientQuickActions } from '../components/client-detail/ClientQuickActions';
import { API } from '../config/api';

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [productions, setProductions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [portalCopied, setPortalCopied] = useState(false);

  const copyPortalLink = async () => {
    try {
      const { data } = await axios.get(`${API}/clients/${id}/portal-link`, { withCredentials: true });
      const url = `${window.location.origin}/portal/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setPortalCopied(true);
      setTimeout(() => setPortalCopied(false), 2500);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to get portal link:', err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [clientRes, prodRes, meetRes, docRes, txnRes] = await Promise.all([
        axios.get(`${API}/clients/${id}`, { withCredentials: true }),
        axios.get(`${API}/productions/client/${id}`, { withCredentials: true }),
        axios.get(`${API}/meetings/client/${id}`, { withCredentials: true }),
        axios.get(`${API}/documents/client/${id}`, { withCredentials: true }),
        axios.get(`${API}/transactions/client/${id}`, { withCredentials: true }),
      ]);
      setClient(clientRes.data);
      setProductions(prodRes.data);
      setMeetings(meetRes.data);
      setDocuments(docRes.data);
      setTransactions(txnRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching client data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarkSigned = useCallback(async (docId) => {
    try {
      await axios.post(
        `${API}/documents/mark-signed`,
        { document_id: docId },
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking document signed:', error);
      }
    }
  }, [fetchData]);

  const handleMarkPaid = useCallback(async (txnId) => {
    try {
      await axios.post(
        `${API}/transactions/mark-paid`,
        { transaction_id: txnId },
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking transaction paid:', error);
      }
    }
  }, [fetchData]);

  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/clients/${id}`, { withCredentials: true });
      // Successfully deleted - redirect to clients list
      navigate('/clients');
    } catch (err) {
      console.error('Failed to delete client:', err);
      alert('Failed to delete client. Please try again.');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-text-secondary">Loading...</div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-alert">Client not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-2xl mx-auto">
        <ClientHeader client={client} />
        
        <div className="p-4 space-y-6">
          <ClientShootsSection clientId={id} productions={productions} />
          <ClientMeetingsSection clientId={id} meetings={meetings} />
          <ClientDocumentsSection 
            clientId={id} 
            documents={documents} 
            onMarkSigned={handleMarkSigned}
          />
          <ClientMoneySection 
            clientId={id} 
            client={client}
            transactions={transactions}
            onMarkPaid={handleMarkPaid}
          />
          <ClientQuickActions clientId={id} />
          
          {/* Client Portal Section */}
          <div className="bg-surface rounded-lg border border-border p-4">
            <button
              onClick={copyPortalLink}
              className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
              data-testid="copy-portal-link-button"
            >
              {portalCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
              <span className="text-sm font-medium">{portalCopied ? 'Copied!' : 'Copy Client Portal Link'}</span>
            </button>
            <p className="text-xs text-text-secondary mt-2">
              Share this link so {client.name.split(' ')[0]} can view their upcoming sessions anytime.
            </p>
          </div>
          
          {/* Delete Client Section */}
          <div className="bg-surface rounded-lg border border-border p-4">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-alert hover:bg-alert/10 rounded-lg transition-colors"
              data-testid="delete-client-button"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-medium">Delete Client</span>
            </button>
            <p className="text-xs text-text-secondary mt-2">
              Permanently delete this client and all related records. This cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-foreground mb-2">Delete {client.name}?</h3>
            <p className="text-sm text-text-secondary mb-6">
              This will permanently delete this client and all linked meetings, shoots, documents, and transactions. 
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-surface-raised disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteClient}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-alert text-white hover:bg-alert/90 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
